'use client'
import {
  Box,
  Text,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Button,
  HStack,
  Image,
  Skeleton,
  Avatar,
  useBreakpointValue,
} from '@chakra-ui/react'
import { useEffect, useCallback } from 'react'
import { formatWatchTime, formatTimeUpload } from '@/components/ui/video-card'
import { BottomNav } from '@/components/ui/bottom-nav'
import { BsSliders } from 'react-icons/bs'
import { FaPlay } from 'react-icons/fa'
import { FaArrowRightLong, FaShuffle } from 'react-icons/fa6'
import { HiBars2 } from 'react-icons/hi2'
import { Icons } from '@/components/icons'
import React from 'react'
import { useInView } from 'react-intersection-observer'
import {
  useWatchlistInfo,
  useWatchlistVideos,
} from '@/src/queries/watchlist/watchlist'
import Link from 'next/link'
import { VideoCardMenu } from '@/components/ui/video-card-menu'
import { useRouter } from 'next/navigation'
import { useSearchParams } from 'next/navigation'
import { getInitials } from '@/components/ui/avatar'
import { secondsToHoursInK } from '@/src/lib/utils'

interface VideoCardProps {
  eventId?: string
  thumbnailUrl?: string
  videoDuration: string
  avatarUrl?: string
  creatorName: string
  creatorId?: string
  title: string
  timelineUpload?: string
  live?: string
  watchCount: string
  watchTime: string
  scheduled?: React.ReactNode
  draft?: boolean
  uploadStatus?: string
  creator?: boolean
  omitDetails?: boolean
  isWatchList?: string
  encryptVideoUrl?: string
  themeColor?: string
  channelShareName?: string
  isVerifiedTick?: string
  sharedImageName?: string
}

const LoadingWatchlistCard = () => {
  return (
    <Flex
      w="100%"
      justifyContent="space-between"
      alignItems="center"
      my={2}
      gap={[2, 2, 4]}
      h="100%"
    >
      <Flex alignItems="center">
        <Icon as={HiBars2} h={[3, 3, 6]} w={[3, 3, 6]} />
      </Flex>
      <Skeleton
        position="relative"
        w={['35%', '35%', 'auto']}
        h={['auto', 'auto', '120px']}
        aspectRatio={16 / 9}
        rounded="xl"
        overflow="hidden"
        bg="gray.300"
      ></Skeleton>
      <Box h={['100%', '100%', '120px']} flex="1">
        <Skeleton h={{ base: '10px', md: '15px' }} w="70%" maxW="300px" m="2" />
        <Skeleton
          h={{ base: '8px', md: '12px' }}
          w="30%"
          maxW="100px"
          minW="20px"
          display={['block', 'block', 'none']}
          m="2"
        />
        <Flex flexDir={['row']} gap={[1, 1, 2]} my={[1, 1, 2]}>
          <Skeleton h={{ base: '8px', md: '12px' }} w="30%" maxW="80px" m="2" />
          <Skeleton h={{ base: '8px', md: '12px' }} w="30%" maxW="80px" m="2" />
        </Flex>
        <Flex alignItems="center" color="lyk-green" columnGap={1}>
          <Skeleton
            h={{ base: '8px', md: '10px' }}
            w="40%"
            maxW="100px"
            m="2"
          />
        </Flex>
      </Box>
    </Flex>
  )
}

const WatchlistCard: React.FC<VideoCardProps> = ({
  eventId,
  thumbnailUrl,
  videoDuration,
  avatarUrl,
  creatorName,
  creatorId,
  title,
  timelineUpload,
  watchCount,
  watchTime,
  live,
  scheduled,
  draft,
  uploadStatus,
  creator,
  omitDetails,
  isWatchList,
  themeColor,
  encryptVideoUrl,
  channelShareName,
  isVerifiedTick,
  sharedImageName,
}) => {
  const videoData = {
    thumbnailUrl,
    videoDuration,
    watchTime,
    title,
    creatorName,
    eventId,
    encryptVideoUrl,
    themeColor,
    sharedImageName,
  }

  const router = useRouter()

  const truncateLength =
    useBreakpointValue({ base: 12, sm: 22, md: 46, lg: title.length }) ||
    title.length

  const truncateTitle = (title: string) => {
    if (title.length > truncateLength) {
      return title.substring(0, truncateLength) + '...'
    }
    return title
  }

  return (
    <Flex
      w="100%"
      justifyContent="space-between"
      alignItems="center"
      my={2}
      gap={[2, 2, 4]}
    >
      <Flex alignItems="center">
        <Icon as={HiBars2} h={[3, 3, 6]} w={[3, 3, 6]} />
      </Flex>
      <Box
        position="relative"
        w={['35%', '35%', 'auto']}
        h={['auto', 'auto', '120px']}
        aspectRatio={16 / 9}
        rounded="xl"
        overflow="hidden"
        bg="gray.300"
        onClick={() => (encryptVideoUrl ? router.push(encryptVideoUrl) : '')}
      >
        {thumbnailUrl && (
          <Image
            src={thumbnailUrl}
            h="full"
            w="full"
            objectFit="cover"
            alt={title}
          />
        )}
      </Box>
      <Box h={['100%', '100%', '120px']} flex="1">
        <Text
          fontSize={['0.9rem', '0.9rem', 'md']}
          fontWeight="500"
          noOfLines={1}
          onClick={() => (encryptVideoUrl ? router.push(encryptVideoUrl) : '')}
          className="cursor-pointer"
        >
          {truncateTitle(title)}
        </Text>

        <Flex
          flexDir={['row']}
          gap={[1, 1, 1.5]}
          my={[1, 1, 2]}
          alignItems="center"
          flexWrap={'wrap'}
        >
          <Avatar
            name={creatorName}
            src={avatarUrl}
            size="sm"
            data-creator-avatar
            bg={themeColor}
            color={'white'}
            getInitials={getInitials}
            onClick={() => router.push('/c/' + channelShareName)}
          />
          <Text
            fontSize={['xs', 'xs', 'sm']}
            color="#3e3e3e90"
            display={['block']}
            className="cursor-pointer"
            onClick={() => router.push('/c/' + channelShareName)}
          >
            {creatorName}&nbsp;&nbsp;
            <Image
              src="/my-channel/verified-tag.svg"
              alt="verified-tag"
              display={isVerifiedTick == '1' ? 'inline' : 'none'}
              h="auto"
              mb={0.5}
              w={[5, 5, 5]}
            />
          </Text>
          <Text
            fontSize={['xs', 'xs', 'sm']}
            color="#3e3e3e90"
            fontWeight="900"
            display={['none', 'none', 'block']}
          >
            &middot;
          </Text>
          {/* <Text color="#3e3e3e90" fontSize={['0.65rem', '0.65rem', 'sm']}>
            57k Watched
          </Text> */}
          {/* <Text
            fontSize={['0.65rem', '0.65rem', 'sm']}
            color="#3e3e3e90"
            fontWeight="900"
          >
            &middot;
          </Text> */}
          <Text color="#3e3e3e90" fontSize={['0.65rem', '0.65rem', 'sm']}>
            {formatTimeUpload(timelineUpload || '')}
          </Text>
        </Flex>
        {Number(watchTime) >= 3600000 &&
          <Flex alignItems="center" color="lyk-green" columnGap={1}>
            <Icons.clock className="color-[inherit] h-3 w-3" />
            <Text fontSize={['0.65rem', '0.65rem', 'sm']} color="inherit">
              {secondsToHoursInK(Number(watchTime))} watched
            </Text>
          </Flex>}
      </Box>
      <Flex h={['100%', '100%', '120px']} alignItems="flex-start">
        <Menu closeOnBlur>
          <MenuButton
            minW={'auto'}
            as={IconButton}
            aria-label="Options"
            icon={<Icons.VerticalDot className="h-6 w-6 text-[#9f9f9f]" />}
            variant="ghost"
            px={0}
            _hover={{ bg: 'transparent' }}
            _active={{ bg: 'transparent' }}
            h={6}
            pt={1}
          />
          <MenuList
            minW={180}
            textColor="lyk-black"
            py={1}
            fontSize={{
              base: 'small',
              sm: 'medium',
            }}
            fontWeight={500}
          >
            <VideoCardMenu
              eventId={eventId}
              creatorId={creatorId}
              isWatchList={isWatchList}
              videoData={videoData}
              channelShareName={channelShareName}
            />
          </MenuList>
        </Menu>
      </Flex>
    </Flex>
  )
}

function formatTotalDuration(totalDuration: string) {
  const hours = Math.floor(parseInt(totalDuration) / (60 * 60))
  if (hours < 1) return 'Less than 1 hour'
  if (hours == 1) return '1 hour'
  return hours.toLocaleString() + ' hours'
}

function formatLastUpdated(createdAt: string) {
  if (!createdAt) {
    return ''
  }

  const curDate = new Date()
  const lastDate = new Date(createdAt.split(' ')[0])
  const difDays = Math.floor(
    Math.abs(curDate.getTime() - lastDate.getTime()) / (24 * 60 * 60 * 1000)
  )
  const difMonths = Math.floor(difDays / 30)
  const difYears = Math.floor(difMonths / 12)
  if (difDays == 0) {
    return 'Updated today'
  } else if (difDays == 1) {
    return 'Updated 1 day ago'
  } else if (difDays < 30) {
    return 'Updated ' + difDays + ' days ago'
  } else if (difMonths < 12) {
    if (difMonths == 1) return 'Updated 1 month ago'
    return 'Updated ' + difMonths + ' months ago'
  } else {
    if (difYears == 1) {
      return 'Updated 1 year ago'
    }
    return 'Updated ' + difYears + ' years ago'
  }
}

export default function Watchlist() {
  const router = useRouter()
  function handlePlayList() {
    router.push(
      firstVideoId +
      '?' +
      createQueryString('list', 'WL') +
      '&' +
      createQueryString('index', '0')
    )
  }

  const sp = useSearchParams()

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(sp?.toString())
      params.set(name, value)
      return params.toString()
    },
    [sp]
  )

  const { data: watchlistInfo } = useWatchlistInfo()

  const { ref, inView } = useInView()
  const result = useWatchlistVideos({ playerWatchlist: false })
  const {
    status,
    data,
    isFetchingNextPage,
    isFetching,
    fetchNextPage,
    hasNextPage,
  } = result
  const hasNextPageRef = React.useRef<boolean>(hasNextPage)

  useEffect(() => {
    hasNextPageRef.current = hasNextPage
  }, [hasNextPage])

  useEffect(() => {
    if (
      inView &&
      hasNextPageRef.current &&
      !isFetchingNextPage &&
      !isFetching
    ) {
      fetchNextPage()
    }
  }, [inView, isFetchingNextPage, fetchNextPage, isFetching])

  let VideoContent: React.ReactNode = null
  if (status == 'pending') {
    VideoContent = (
      <>
        {Array(2)
          .fill('-')
          .map((_, i) => (
            <LoadingWatchlistCard key={i} />
          ))}
      </>
    )
  }

  if (data?.pages.flatMap((page) => page.videos).length === 0) {
    return (
      <Box
        mt={12}
        px={[4, 4, 4]}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flex="1"
        flexDir={['column', 'column', 'row']}
        w="100%"
        gap={6}
      >
        <Image
          src="/no-subs-man.png"
          w={[200, 200, 250, 300]}
          alt="No Subscriptions"
          loading="eager"
          fetchPriority="high"
        />
        <Box maxW="400px" w={['80%', '80%', '100%']}>
          <Text fontSize={['xl', 'xl', '2xl']} fontWeight="bold">
            Uh Oh! Looks like you don&apos;t have any videos in your watchlist.
          </Text>
          <Text mt={2}>Add videos to your watchlist to watch them later.</Text>
          <Button
            bg="lyk-green"
            color="white"
            _hover={{ bg: 'lyk-green' }}
            rightIcon={<Icon as={FaArrowRightLong} />}
            size="sm"
            px={8}
            py={6}
            mt={5}
            as={Link}
            href={'/home'}
          >
            Go to Home
          </Button>
        </Box>
      </Box>
    )
  }

  let firstVideoUrl = ''
  let firstVideoId = ''
  if (status == 'success') {
    if (!firstVideoUrl) {
      firstVideoUrl = data?.pages[0].videos[0]?.thumbnailImage
      firstVideoId = data?.pages[0].videos[0]?.encryptVideoUrl
    }
    VideoContent = data?.pages?.map((page, i) => (
      <React.Fragment key={i}>
        {page?.videos?.map((video) => (
          <WatchlistCard
            key={video.eventId}
            eventId={video.eventId}
            creatorId={video.channelId}
            videoDuration={video.duration}
            avatarUrl={video.imageUrl}
            creatorName={video.channelName}
            title={video.eventTitle}
            watchTime={video.UniqueWatchTime}
            watchCount={video.watchCount}
            thumbnailUrl={video.thumbnailImage}
            sharedImageName={video.sharedImageName || video.thumbnailImage}
            timelineUpload={video.eventEntryDate}
            live={video.type == '2' ? 'live' : ''}
            isWatchList={video.isWatchList}
            encryptVideoUrl={video.encryptVideoUrl}
            channelShareName={video.channelShareName}
            isVerifiedTick={video.isVerifiedTick}
          />
        ))}
      </React.Fragment>
    ))
  }

  if (status == 'error') {
    return (
      <Flex
        w="100%"
        flex="1"
        flexDir="column"
        align="center"
        justify="center"
        mt={40}
      >
        <Image src="/failed.svg" width={150} height={150} alt="Failed Icon" />
        <Text mt={4} color="black" fontWeight={600} fontSize={18}>
          Failed to load videos!
        </Text>
        <Button
          _hover={{ opacity: 0.5 }}
          bg={'lyk-green'}
          color={'white'}
          fontSize={{
            base: 'sm',
            md: 'md',
          }}
          href={'/home'}
          as={Link}
          mt={4}
        >
          Go back
        </Button>
      </Flex>
    )
  }

  return (
    <Flex justify={'flex-end'} minH="100vh">
      <Box w="100%" zIndex={2} pb={[20, 10]}>
        <Box pb={10} px={[4, 4, 4, 4]} mt={4}>
          <Flex
            flexDir={['column', 'column', 'row']}
            alignItems={['stretch', 'stretch', 'center']}
            w="100%"
            columnGap={4}
            rowGap={4}
            pos="relative"
          >
            <Menu closeOnBlur>
              <MenuButton
                minW={'auto'}
                as={Button}
                aria-label="Options"
                leftIcon={<Icon as={BsSliders} h={[4]} w={[4]} />}
                variant="ghost"
                hidden
                px={0}
                _hover={{ bg: 'transparent' }}
                _active={{ bg: 'transparent' }}
                fontWeight="400"
                position="absolute"
                top={2}
                right={2}
                fontSize={['sm', 'sm', 'md']}
              >
                Sort
              </MenuButton>
              <MenuList
                minW={180}
                textColor="lyk-black"
                py={1}
                fontWeight={500}
              >
                <MenuItem
                  opacity={0.75}
                  _hover={{ bg: '#ECECEC' }}
                  fontSize="sm"
                  py={2}
                >
                  Date Added (new)
                </MenuItem>
                <MenuItem
                  opacity={0.75}
                  _hover={{ bg: '#ECECEC' }}
                  fontSize="sm"
                  py={2}
                >
                  Date Added (old)
                </MenuItem>
                <MenuItem
                  opacity={0.75}
                  _hover={{ bg: '#ECECEC' }}
                  fontSize="sm"
                  py={2}
                >
                  Most Popular
                </MenuItem>
                <MenuItem
                  opacity={0.75}
                  _hover={{ bg: '#ECECEC' }}
                  fontSize="sm"
                  py={2}
                >
                  Date Published (new)
                </MenuItem>
                <MenuItem
                  opacity={0.75}
                  _hover={{ bg: '#ECECEC' }}
                  fontSize="sm"
                  py={2}
                >
                  Date Published (old)
                </MenuItem>
              </MenuList>
            </Menu>
            <Box
              position="relative"
              w={['100%', '100%', '40%']}
              h="auto"
              aspectRatio={16 / 9}
              rounded="xl"
              overflow="hidden"
              bg="gray.300"
              bgImage={`url(${firstVideoUrl})`}
              bgRepeat={'no-repeat'}
              bgSize={'cover'}
              bgPosition={'center'}
            ></Box>
            <Flex
              flexDir={['row', 'row', 'column']}
              alignItems={['center', 'center', 'flex-start']}
              justifyContent={'space-between'}
              gap={0}
            >
              <Box>
                <Text
                  fontSize={['xl', 'xl', '3xl']}
                  fontWeight="600"
                  py={0}
                  my={0}
                >
                  Watchlist
                </Text>
                <HStack gap={[1, 1, 2]} mt={[1, 1, 1]}>
                  <Text fontSize={['xs', 'xs', 'md']} color="#3e3e3e">
                    {(watchlistInfo?.watchlistCount || 0) + ' Videos'}
                  </Text>
                  <Text
                    fontSize={['xs', 'xs', 'md']}
                    color="#3e3e3e"
                    fontWeight="900"
                  >
                    &middot;
                  </Text>
                  <Text color="#3e3e3e" fontSize={['xs', 'xs', 'md']}>
                    {formatLastUpdated(watchlistInfo?.createdAt || '')}
                  </Text>
                </HStack>
                <Text color="#3e3e3e" fontSize={['xs', 'xs', 'md']}>
                  {formatTotalDuration(watchlistInfo?.totalDuration || '0')}
                </Text>
              </Box>
              <Flex
                flexDir={['column', 'column', 'row']}
                alignItems="center"
                justifyContent="center"
                columnGap={2}
                rowGap={2}
                mt={3}
                h="100%"
              >
                <Button
                  leftIcon={<Icon as={FaPlay} />}
                  rounded="full"
                  px={[0, 0, 4]}
                  bg="lyk-green"
                  color="white"
                  _hover={{ bg: 'lyk-green' }}
                  size={['sm', 'sm', 'md']}
                  w={['110px', '110px', 'auto']}
                  onClick={handlePlayList}
                >
                  Play List
                </Button>
                <Button
                  leftIcon={<Icon as={FaShuffle} />}
                  rounded="full"
                  hidden
                  px={4}
                  bg="lyk-red"
                  color="white"
                  _hover={{ bg: 'lyk-red' }}
                  size={['sm', 'sm', 'md']}
                  w={['110px', '110px', 'auto']}
                >
                  Shuffle
                </Button>
              </Flex>
            </Flex>
          </Flex>

          <Flex w="100%" display={['none']}>
            <Menu closeOnBlur>
              <MenuButton
                minW={'auto'}
                as={Button}
                aria-label="Sort"
                leftIcon={<Icon as={BsSliders} h={[4]} w={[4]} />}
                variant="ghost"
                px={0}
                _hover={{ bg: 'transparent' }}
                _active={{ bg: 'transparent' }}
                fontWeight="400"
                fontSize={['sm', 'sm', 'md']}
              >
                Sort
              </MenuButton>
              <MenuList
                minW={180}
                textColor="lyk-black"
                py={1}
                fontWeight={500}
              >
                <MenuItem
                  opacity={0.75}
                  _hover={{ bg: '#ECECEC' }}
                  fontSize="sm"
                  py={2}
                >
                  Date Added (new)
                </MenuItem>
                <MenuItem
                  opacity={0.75}
                  _hover={{ bg: '#ECECEC' }}
                  fontSize="sm"
                  py={2}
                >
                  Date Added (old)
                </MenuItem>
                <MenuItem
                  opacity={0.75}
                  _hover={{ bg: '#ECECEC' }}
                  fontSize="sm"
                  py={2}
                >
                  Most Popular
                </MenuItem>
                <MenuItem
                  opacity={0.75}
                  _hover={{ bg: '#ECECEC' }}
                  fontSize="sm"
                  py={2}
                >
                  Date Published (new)
                </MenuItem>
                <MenuItem
                  opacity={0.75}
                  _hover={{ bg: '#ECECEC' }}
                  fontSize="sm"
                  py={2}
                >
                  Date Published (old)
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>

          <Flex w="100%" flexDir="column" mt={4}>
            {VideoContent}
            {isFetching &&
              Array(2)
                .fill('-')
                .map((_, i) => <LoadingWatchlistCard key={i} />)}
            <div ref={ref} className="pb-10"></div>
          </Flex>
        </Box>
      </Box>
      <BottomNav />
    </Flex>
  )
}